'use client';

import { type PlantBasicDTO } from '@/domain/country/types';
import { usePlantsBasic } from '@/domain/plant/hooks';
import { type RoadMapUpdate } from '@/domain/roadmap/types';
import { useAuth } from '@/hooks/useAuth';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { type Control, Controller } from 'react-hook-form';

import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

interface CustomersFieldProps {
  viewOnly: boolean;
  control: Control<RoadMapUpdate, unknown>;
  required: boolean;
}

export function CustomersField({ viewOnly, control, required }: Readonly<CustomersFieldProps>) {
  const { user } = useAuth();
  const { data: plants } = usePlantsBasic(!!user);

  return (
    <Box sx={{ py: 0.75, width: '100%' }}>
      <Controller
        name="customers"
        control={control}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <FieldActionsWrapper>
              <Autocomplete<PlantBasicDTO, true, false, false>
                multiple
                value={value ?? []}
                onChange={(_, newValue) => onChange(newValue)}
                options={plants ?? []}
                getOptionLabel={(plant) => plant.plant}
                isOptionEqualToValue={(opt, v) => opt.idPlant === v.idPlant}
                disabled={viewOnly}
                label={`Customers${required ? ' *' : ''}`}
                placeholder="Search customers..."
                fullWidth
                size="small"
                sx={error && required ? { '& .MuiOutlinedInput-root': { borderColor: 'error.main' } } : undefined}
              />
            </FieldActionsWrapper>
            {error && required && (
              <Typography variant="caption" color="error.main">
                Required
              </Typography>
            )}
          </>
        )}
      />
    </Box>
  );
}
